import Modal from 'antd/lib/modal/Modal';
import CloseIcon from 'assets/icons/CloseIcon';
import React, { useEffect, useState } from 'react';
import { useAppTranslation } from 'app/hooks';
import useUserDetail from 'hooks/useUserDetail';
import useThemes from 'hooks/useThemes';
import { Form } from 'antd';
import VerticalForm from 'components/Form/VerticalForm';
import TextField from 'components/FormField/TextField';
import FieldError from 'components/FormField/FieldError';
import { ReferralRequestBody } from 'models/user';
import userApi from 'api/userApi';
import { showSuccessModal } from 'components/Modals/CommonModals';
import { useHandleResponseError } from 'hooks/useHandleResponseError';

interface ReferralCodeModalProps {
  title?: string;
  visible: boolean;
  closeModal: () => void;
  className?: string;
  referralCode: string;
}

const ReferralCodeModal: React.FunctionComponent<ReferralCodeModalProps> = (props) => {
  const { title = '', visible, closeModal, className = '' } = props;
  const t = useAppTranslation();
  const { getUser } = useUserDetail();
  const { currentTheme } = useThemes();

  const [form] = Form.useForm();
  const handleResponseError = useHandleResponseError();

  const [haveError, setHaveError] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);

  const onSubmit = async (value: ReferralRequestBody) => {
    const { ok, error } = await userApi.updateReferralCode(value);
    if (ok) {
      getUser();
      closeModal();
      showSuccessModal({
        title: t('referral-code.success'),
        content: '',
      });
    }
    if (error) {
      handleResponseError(error);
    }
  };

  const handleFieldsChange = () => {
    setHaveError(form.getFieldsError().some((error) => error.errors.length));
  };

  useEffect(() => {
    form.setFieldValue('referralCode', props.referralCode);
    return () => form.resetFields();
  }, [props.referralCode, visible]);

  useEffect(() => {
    if (form.getFieldValue('referralCode') === props.referralCode) {
      return setIsDirty(true);
    }
    setIsDirty(false);
  }, [form.getFieldValue('referralCode')]);

  return (
    <Modal
      className={className}
      title={title}
      visible={visible}
      onCancel={closeModal}
      footer={null}
      centered
      maskClosable={false}
      closeIcon={<CloseIcon isDarkMode={currentTheme === 'dark'} width="20" height="20" />}
      width={500}
    >
      <VerticalForm
        name=" auth__change-password referralCode"
        className="referral-code-form"
        form={form}
        onFinish={onSubmit}
        onFieldsChange={handleFieldsChange}
      >
        <TextField
          name="referralCode"
          defaultValue={props.referralCode}
          className="referral-input"
          rules={[
            {
              required: true,
              message: <FieldError message={t('errors.referral-code.required')} />,
            },
            {
              min: 6,
              message: <FieldError message={t('errors.referral-code.min-max')} />,
            },
            {
              max: 50,
              message: <FieldError message={t('errors.referral-code.min-max')} />,
            },
            {
              pattern: /^(?=.*[a-zA-Z])(?=.*[0-9])[a-zA-Z0-9]+$/,
              message: <FieldError message={t('errors.referral-code.invalid')} />,
            },
          ]}
        />
        <button
          disabled={haveError || isDirty}
          className={`btn-border-submit ${haveError || isDirty ? 'disabled' : ''}`}
          type="submit"
        >
          {t('common.edit')}
        </button>
      </VerticalForm>
    </Modal>
  );
};

export default ReferralCodeModal;
